import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signOut 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

// Création du contexte
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => {
  return useContext(AuthContext);
};

// Fournisseur du contexte
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fonction de déconnexion
  const logout = () => {
    return signOut(auth);
  };

  // Effet pour surveiller l'état de l'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Vérifier si l'utilisateur est un agriculteur
          const farmerDoc = await getDoc(doc(db, "farmers", user.uid));
          
          if (farmerDoc.exists()) {
            // Si l'utilisateur est un agriculteur, stocker ses données
            const farmerData = farmerDoc.data();
            setCurrentUser({ ...user, ...farmerData });
            setUserRole('farmer');
          } else {
            // Vérifier si l'utilisateur est un acheteur
            const buyerDoc = await getDoc(doc(db, "buyers", user.uid));
            
            if (buyerDoc.exists()) {
              // Si l'utilisateur est un acheteur, stocker ses données
              const buyerData = buyerDoc.data();
              setCurrentUser({ ...user, ...buyerData });
              setUserRole('buyer');
            } else {
              try {
                // Vérifier si l'utilisateur est un administrateur ou PDG
                const userDoc = await getDoc(doc(db, "users", user.uid));
                
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  setCurrentUser({ ...user, ...userData });
                  setUserRole(userData.role); // 'admin' ou 'PDG'
                } else {
                  // L'utilisateur n'a pas de rôle spécifique
                  setCurrentUser(user);
                  setUserRole(null);
                }
              } catch (userError) {
                console.error("Erreur lors de la récupération des données utilisateur:", userError);
                // En cas d'erreur de permission, on vérifie si l'utilisateur est PDG
                // Cette approche est nécessaire car les règles de sécurité peuvent bloquer l'accès
                setCurrentUser(user);
                setUserRole(null);
              }
            }
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des données utilisateur:", error);
          setCurrentUser(user);
          setUserRole(null);
        }
      } else {
        // Aucun utilisateur connecté
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    // Nettoyer l'abonnement lors du démontage
    return unsubscribe;
  }, []);

  // Valeur du contexte
  const value = {
    currentUser,
    userRole,
    logout,
    isAuthenticated: !!currentUser,
    isFarmer: userRole === 'farmer',
    isBuyer: userRole === 'buyer'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;